import React from 'react';

import config from '../../config'; 
import { connect } from 'react-redux';
import { optimizedImage } from '../products/CroppedProductImage';
import { trackEvent } from '../analytics/tracking';
import { DispensaryContext } from './DispensaryProvider';
import { setIsPickup } from '../actions/cartActions';
import { getFormattedAddress, useDistanceMatrix } from '../googlemaps/google-maps-utils';
// import OptimizedImage, { ImageType } from '../common/OptimizedImage';
import GoogleMap from '../googlemaps/GoogleMap';
import PickupDetails from './PickupDetails';
import DeliveryDetails from './DeliveryDetails';
import DeliveryWindowsModal from '../deliveryWindows/DeliveryWindowsModal';
import CurrentTimeProvider from '../timeSlots/CurrentTimeProvider';
import DisplayPickupSlotsModal from '../timeSlots/DisplayPickupSlotsModal';
// import DeliveryCreditBanner from '../promos/DeliveryCreditBanner';
import DisplayModal from '../common/DisplayModal';
import PickupSlotsProvider from '../timeSlots/PickupSlotsProvider';
import PreviewMessage from '../preview/PreviewMessage';
import DispensaryMessage from './DispensaryMessage';
import PrimaryHeader from '../styleguide/PrimaryHeader';
import SecondaryHeader from '../styleguide/SecondaryHeader';
// import DispensaryLogo from '../dispensary/DispensaryLogo';

import PropTypes from 'prop-types';
import styles from './Header.module.css';

const DispensaryHeader = ({
  delivery_fee_cents,
  deliveryAddress,
  anonymousZipCode,
  isPickup,
  setIsPickup
}) => {

  const { dispensary, loading } = React.useContext(DispensaryContext);
  const [deliveryDateOffset, setDeliveryDateOffset] = React.useState(0);

  const [showMap, setShowMap] = React.useState();

  const [deliveryFee, setDeliveryFee] = React.useState();
  const [showDeliveryWindows, setShowDeliveryWindows] = React.useState();
  
  React.useEffect(() => {
    if (dispensary?.id) {
      // Get TODAY's business hours (json)
      const getWeekdayInfo = () => {
        const weekday = new Date().toLocaleDateString('en-US', { weekday: 'long' }).toLocaleLowerCase();
        // Start time of 9:01 makes the fallback value obvious.
        const weekdayInfo = dispensary.hours_of_operation.length 
          ? dispensary.hours_of_operation.find((day) => day['weekday'] === weekday) 
          : [{ hours: {start_hour:9, start_minute:1, end_hour:17, end_minute:1}}];
        return weekdayInfo;
      };

      const currentHour = new Date().getHours();
      const weekdayInfo = getWeekdayInfo();
      // TODO: Fix me - hard-coded 2
      const dateOffset = (currentHour + 2) >= weekdayInfo.hours.end_hour ? 1 : 0;
      setDeliveryDateOffset(dateOffset);
    }
  }, [dispensary]);

  const handleShowDeliverySchedule = () => {
    trackEvent("delivery_window_modal", "show_modal", dispensary.id);
    setShowDeliveryWindows(true);
  };

  const [showPickupWindows, setShowPickupWindows] = React.useState();
  
  const handleShowPickupSchedule = () => {
    trackEvent("pickup_window_modal", "show_modal", dispensary.id);
    setShowPickupWindows(true);
  };

  const userAddressString = getFormattedAddress(deliveryAddress);
  const [dispensaryAddress, setDispensaryAddress] = React.useState();

  // Google Maps API - show distance for Pickup orders
  const [travelDistance, setTravelDistance] = React.useState();
  useDistanceMatrix(userAddressString, dispensaryAddress, setTravelDistance);

  React.useEffect(() => {
    if (!deliveryFee && delivery_fee_cents) {
      setDeliveryFee(`$${parseFloat(delivery_fee_cents/100).toFixed(2)}`);
    }
  }, [delivery_fee_cents, deliveryFee]);

  // Handle dispensary images
  const [heroImg, setHeroImg] = React.useState();
  const [logoImg, setLogoImg] = React.useState();
  React.useEffect(() => {
    if (dispensary?.image_urls) {
      setHeroImg(optimizedImage(dispensary.image_urls.primary, 1200));
      setLogoImg(optimizedImage(dispensary.image_urls.secondary));
    }
    if (dispensary?.location) {
      setDispensaryAddress(getFormattedAddress(dispensary.location)); 
    }
  }, [dispensary]);
  
  return (
    loading
      ? <div style={{height:'400px'}}></div>   /* reserve space */
      : <div className={styles.dispensaryHeader}>
          <div className={styles.heroWrap}>
            <img alt={dispensary.name} src={heroImg} className={styles.dispensaryHero} />
            <img alt={dispensary.name} src={logoImg} className={styles.dispensaryLogo} />
          </div>
    
          <div className={styles.dispensaryInfo}>
            <div className={styles.nameAndAddress}>
              <PrimaryHeader text={dispensary.name} >
                <div className={styles.dispensaryAddress}>
                  <span>{`${dispensary.location.street_address}, `}</span>
                  <span>
                    { dispensary.location.apartment_number && 
                      `${dispensary.location.apartment_number}, `
                    }
                  </span>
                  <span>{`${dispensary.location.city}, `}</span>
                  <span>{`${dispensary.location.state}, `}</span>
                  <span>{dispensary.location.zip_code}</span>
                  { travelDistance &&
                    <span> &middot;&nbsp;<b>{travelDistance}</b></span>
                  }
                </div>
              </PrimaryHeader>
            </div>
    
            <div className={styles.detailsGrid}>
              { config.IS_PREVIEW_MODE
                ? <PreviewMessage />
                : isPickup
                  ? <PickupDetails
                      travelDistance={travelDistance}
                      handleShowMap={setShowMap}
                      handlePickupToggle={setIsPickup}
                      handleShowSchedule={handleShowPickupSchedule} />
                  : <DeliveryDetails
                      deliveryFee={deliveryFee}
                      deliveryDateOffset={deliveryDateOffset}
                      handlePickupToggle={setIsPickup}
                      handleShowSchedule={handleShowDeliverySchedule} />
              }
            </div>
          </div>
    
          <CurrentTimeProvider>
            <PickupSlotsProvider dispensaryId={dispensary.id}>
              {/* delivery not available, etc. */}
              <div className={styles.messageWrap}>
                <DispensaryMessage
                  dispensaryId={dispensary.id}
                  dispensaryName={dispensary.name}
                  dispensaryLocation={dispensary.location}
                  deliveryZipCodes={dispensary.delivery_zip_codes || []} /* API is missing this ATM */
                  isPickup={isPickup}
                  deliveryAddress={deliveryAddress}
                  anonymousZipCode={anonymousZipCode} />
              </div>
            </PickupSlotsProvider>
          
            {/*
              <div className={styles.messageWrap}>
                <DeliveryCreditBanner />
              </div>
            */}
          
            { showPickupWindows &&
              <DisplayModal title="Pickup Timeslots" showAboveNav closeFn={() => setShowPickupWindows(false)}> 
                <DisplayPickupSlotsModal 
                  dispensaryId={dispensary.id} 
                  dispensaryName={dispensary.name} 
                  logoImage={logoImg} />
              </DisplayModal>
            }
    
            { showDeliveryWindows &&
              <DisplayModal title="Delivery Windows" showAboveNav closeFn={() => setShowDeliveryWindows(false)}>
                <DeliveryWindowsModal 
                  dispensaryId={dispensary.id} 
                  dispensaryName={dispensary.name} 
                  logoImage={logoImg} />
              </DisplayModal>
            }
          </CurrentTimeProvider>
          { showMap &&
            <DisplayModal title="Dispensary Location" showAboveNav closeFn={() => setShowMap(false)}>
              <SecondaryHeader isCentered text={dispensary.name} />
              <div style={{textAlign:'center'}}>
                {dispensaryAddress}
              </div>
              <GoogleMap address={dispensaryAddress} />
            </DisplayModal>
          }
        </div>
  );
} 

DispensaryHeader.propTypes = {
  delivery_fee_cents: PropTypes.number,
  deliveryAddress: PropTypes.object,
  anonymousZipCode: PropTypes.string,
  isPickup: PropTypes.bool
};

const mapStateToProps = (state)=>{
  return {
    delivery_fee_cents: state.delivery_fee_cents,
    deliveryAddress: state.delivery_address,
    anonymousZipCode: state.anonymous_zip_code,
    isPickup: state.is_pickup,
  }
}

const mapDispatchToProps = (dispatch)=>{
  return{
    setIsPickup: (isPickup)=>{dispatch(setIsPickup(isPickup))}
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(DispensaryHeader)